'use client';

import {type FC, memo, useMemo} from 'react';
import {Modal} from '@/utils/antd-imports';
import type {FirstTimePeriodEntryModalReward} from './FirstTimePeriodEntryModal.types';
import {FirstTimePeriodEntryModalView} from './FirstTimePeriodEntryModalView';

/**
 * @description Оболочка модалки «1st Time Period Entry» (Ant Design Modal + разметка как у CelebrityShareSuccessModal).
 */
export interface FirstTimePeriodEntryModalOptions {
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
  ariaLabel?: string;
  /**
   * Дополнительные классы на корневой Modal (ant-modal-wrap / content).
   */
  className?: string;
  /**
   * Дополнительные классы на внутреннюю карточку (#1E1E2A).
   */
  panelClassName?: string;
  onClose?: () => void;
}

interface FirstTimePeriodEntryModalProps {
  open: boolean;
  onClose: () => void;
  options?: FirstTimePeriodEntryModalOptions;
  reward: FirstTimePeriodEntryModalReward | null;
}

const DEFAULT_OPTIONS: Required<Pick<FirstTimePeriodEntryModalOptions, 'closeOnBackdropClick' | 'closeOnEscape'>> = {
  closeOnBackdropClick: true,
  closeOnEscape: true,
};

const MODAL_SHELL_CLASSNAME =
  '[&_.ant-modal-content]:!bg-transparent [&_.ant-modal-content]:!p-0 [&_.ant-modal-wrap]:!bg-black/60 [&_.ant-modal-content]:max-w-[288px] md:[&_.ant-modal-content]:max-w-[560px] [&_.ant-modal-content]:mx-auto';

export const FirstTimePeriodEntryModal: FC<FirstTimePeriodEntryModalProps> = memo(
  ({open, onClose, options, reward}) => {
    const mergedOptions = useMemo(() => {
      return {
        ...DEFAULT_OPTIONS,
        ...(options ?? {}),
      };
    }, [options]);

    const modalClassName = [MODAL_SHELL_CLASSNAME, mergedOptions.className].filter(Boolean).join(' ');

    const panelClassName = [
      `relative flex flex-col rounded-[24px] bg-[#1E1E2A] px-6 py-7 md:p-8 font-['Inter']`,
      mergedOptions.panelClassName,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        centered
        closable={false}
        maskClosable={mergedOptions.closeOnBackdropClick}
        keyboard={mergedOptions.closeOnEscape}
        className={modalClassName}
        styles={{
          body: {
            padding: 0,
          },
        }}
        width="100%"
        aria-label={mergedOptions.ariaLabel ?? '1st time period entry'}
        destroyOnClose
      >
        <div className={panelClassName}>
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 flex size-10 items-center justify-center rounded-full border border-[rgba(233,236,240,0.2)] p-2 transition-opacity hover:opacity-80 md:right-4 md:top-4"
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <title>Close</title>
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="#E9ECF0"
                strokeWidth="1.35"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          {reward ? <FirstTimePeriodEntryModalView reward={reward} onClose={onClose} /> : null}
        </div>
      </Modal>
    );
  },
);

FirstTimePeriodEntryModal.displayName = 'FirstTimePeriodEntryModal';
